import React, { useState, useRef, useEffect } from 'react';
import { getApiBaseUrl } from '../../services/api';
import './DictationOverlay.css';

const API_BASE_URL = getApiBaseUrl();

const DictationOverlay = ({ article, isOpen, onClose, onSaveMistakes }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(0.75); // Start with 0.75x for better dictation clarity
  const [autoPause, setAutoPause] = useState(false);
  const [dictationText, setDictationText] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [replayCount, setReplayCount] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);
  
  // Dictation-specific states
  const [dictationMode, setDictationMode] = useState('normal'); // 'normal', 'slow', 'paused'
  const [currentSentence, setCurrentSentence] = useState(0);
  const [sentences, setSentences] = useState([]);
  const [repetitionCount, setRepetitionCount] = useState(0);
  const [maxRepetitions, setMaxRepetitions] = useState(2);

  const audioRef = useRef(null);
  const textareaRef = useRef(null);

  // Update word count when text changes
  useEffect(() => {
    const words = dictationText.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [dictationText]);

  // Handle audio time updates
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    
    // Auto-pause functionality
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      
      // Auto-pause after sentences when enabled
      if (autoPause && sentences.length > 0 && currentSentence < sentences.length) {
        const currentSentenceText = sentences[currentSentence];
        
        // More sophisticated sentence timing based on audio duration and sentence position
        if (audio.duration && audio.duration > 0) {
          const totalDuration = audio.duration;
          const sentenceCount = sentences.length;
          const estimatedSentenceDuration = totalDuration / sentenceCount;
          const currentSentenceStart = currentSentence * estimatedSentenceDuration;
          const currentSentenceEnd = (currentSentence + 1) * estimatedSentenceDuration;
          
          // Pause when we reach the estimated end of current sentence
          if (audio.currentTime >= currentSentenceEnd - 0.5) { // Pause 0.5 seconds before estimated end
            audio.pause();
            setIsPlaying(false);
            console.log(`⏸️ Auto-paused after sentence ${currentSentence + 1}: "${currentSentenceText}"`);
            
            // Show a brief message to the user
            setTimeout(() => {
              // You could add a toast notification here if you want
              console.log('💡 Auto-paused: Time to write this sentence!');
            }, 100);
          }
        }
      }
    };
    
    const handleError = (e) => {
      console.error('Audio error:', e);
      console.error('Audio src:', audio.src);
      console.error('Article audio_path:', article?.audio_path);
      
      // Check if the error is due to file not found or path issues
      if (audio.error) {
        switch (audio.error.code) {
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            setAudioError('Audio file format not supported or file not found');
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            setAudioError('Network error loading audio file');
            break;
          case MediaError.MEDIA_ERR_ABORTED:
            setAudioError('Audio loading was aborted');
            break;
          default:
            setAudioError('Failed to load audio file');
        }
      } else {
        setAudioError('Failed to load audio file');
      }
      setAudioLoading(false);
    };
    const handleLoadStart = () => {
      console.log('🎵 Audio loading started');
      setAudioLoading(true);
      setAudioError(null);
    };
    const handleCanPlay = () => {
      console.log('🎵 Audio can play, duration:', audio.duration);
      setAudioLoading(false);
      setAudioError(null);
    };
    const handleLoadedMetadata = () => {
      console.log('🎵 Audio metadata loaded, duration:', audio.duration);
      setAudioLoading(false);
      setAudioError(null);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', () => setIsPlaying(false));
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', () => setIsPlaying(false));
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [article?.audio_path, autoPause, sentences, currentSentence]);

  // Create proper audio URL
  const getAudioUrl = () => {
    if (!article?.audio_path) return '';
    
    // If it's already a full URL, use it as is
    if (article.audio_path.startsWith('http://') || article.audio_path.startsWith('https://')) {
      return article.audio_path;
    }
    
    // If it's a Windows file path (contains backslashes or drive letter), convert it
    if (article.audio_path.includes('\\') || article.audio_path.includes(':')) {
      // Extract the relative path from the uploads directory
      const uploadsIndex = article.audio_path.indexOf('uploads');
      if (uploadsIndex !== -1) {
        // Get everything after 'uploads' and convert backslashes to forward slashes
        const relativePath = article.audio_path.substring(uploadsIndex + 8).replace(/\\/g, '/');
        // Construct URL to backend server (port 5000) using the /uploads route
        return `${API_BASE_URL}/uploads/${relativePath}`;
      }
    }
    
    // If it's a relative path starting with uploads, construct URL to backend
    if (article.audio_path.startsWith('uploads') || article.audio_path.startsWith('/uploads')) {
      const cleanPath = article.audio_path.replace(/^\/?uploads\/?/, '');
      return `${API_BASE_URL}/uploads/${cleanPath}`;
    }
    
    // If it's a relative path, construct the full URL
    if (article.audio_path.startsWith('/')) {
      // Remove the leading slash and construct URL relative to current domain
      return `${window.location.origin}${article.audio_path}`;
    }
    
    // If it's a relative path without leading slash, add it
    return `${window.location.origin}/${article.audio_path}`;
  };

  // Format time for display
  const formatTime = (time) => {
    if (isNaN(time) || time === Infinity) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Audio control functions
  const togglePlay = async () => {
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Ensure audio is loaded before playing
        if (audioRef.current.readyState < 2) {
          console.log('🎵 Audio not ready, loading...');
          await audioRef.current.load();
        }
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('❌ Error controlling audio:', error);
      console.error('Audio element:', audioRef.current);
      console.error('Audio src:', article?.audio_path);
    }
  };

  // Dictation-specific audio controls
  const startDictationMode = () => {
    console.log('🚀 startDictationMode called');
    console.log('📊 Current state:', { sentences: sentences.length, currentSentence, dictationMode });
    
    if (sentences.length === 0) {
      console.log('❌ No sentences available, cannot start dictation');
      return;
    }
    
    setDictationMode('normal');
    setCurrentSentence(0);
    setRepetitionCount(0);
    console.log('✅ Starting dictation mode');
    playCurrentSentence();
  };

  const playCurrentSentence = () => {
    console.log('🎵 playCurrentSentence called');
    console.log('📊 Current state:', { currentSentence, sentencesLength: sentences.length });
    
    if (currentSentence >= sentences.length) {
      // Dictation complete
      console.log('✅ Dictation complete!');
      setDictationMode('paused');
      setIsPlaying(false);
      return;
    }

    const sentence = sentences[currentSentence];
    console.log(`📝 Playing sentence ${currentSentence + 1}: "${sentence}"`);
    
    // For now, we'll play the full audio since we don't have sentence timestamps
    // In a real implementation, you'd have sentence start/end times from the TTS API
    if (audioRef.current) {
      // Reset to beginning and play
      audioRef.current.currentTime = 0;
      audioRef.current.play();
      setIsPlaying(true);
      console.log('🎵 Audio started playing');
    } else {
      console.log('❌ Audio ref is null');
    }
  };

  const repeatCurrentSentence = () => {
    const newRepetitionCount = repetitionCount + 1;
    setRepetitionCount(newRepetitionCount);
    setReplayCount(prev => prev + 1);
    
    console.log(`🔄 Repeating sentence ${currentSentence + 1}, repetition ${newRepetitionCount + 1}/${maxRepetitions}`);
    
    if (newRepetitionCount >= maxRepetitions) {
      // Move to next sentence after max repetitions
      console.log(`⏭️ Max repetitions reached, moving to next sentence`);
      nextSentence();
    } else {
      // Repeat current sentence
      playCurrentSentence();
    }
  };

  const nextSentence = () => {
    if (currentSentence < sentences.length - 1) {
      const newSentenceIndex = currentSentence + 1;
      setCurrentSentence(newSentenceIndex);
      setRepetitionCount(0);
      setDictationMode('normal');
      
      console.log(`⏭️ Moving to sentence ${newSentenceIndex + 1}: "${sentences[newSentenceIndex]}"`);
      
      // Pause briefly before next sentence
      setTimeout(() => {
        playCurrentSentence();
      }, 1000);
    } else {
      // Dictation complete
      console.log(`✅ Dictation complete! All sentences finished.`);
      setDictationMode('paused');
      setIsPlaying(false);
    }
  };

  const previousSentence = () => {
    if (currentSentence > 0) {
      const newSentenceIndex = currentSentence - 1;
      setCurrentSentence(newSentenceIndex);
      setRepetitionCount(0);
      setDictationMode('normal');
      
      console.log(`⏮️ Moving to sentence ${newSentenceIndex + 1}: "${sentences[newSentenceIndex]}"`);
      playCurrentSentence();
    }
  };

  const changeDictationSpeed = () => {
    const speeds = [0.5, 0.75, 1];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newSpeed = speeds[nextIndex];
    setPlaybackSpeed(newSpeed);
    
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
    
    console.log('⚡ Dictation speed changed to:', newSpeed + 'x');
  };

  const replayLastSentence = () => {
    try {
      // Simple replay - go back 5 seconds
      const newTime = Math.max(0, currentTime - 5);
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setReplayCount(prev => prev + 1);
      console.log('🔁 Replayed to:', formatTime(newTime));
    } catch (error) {
      console.error('❌ Error replaying audio:', error);
    }
  };

  const seekTo = (e) => {
    try {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percent = clickX / rect.width;
      const newTime = percent * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      console.log('🎯 Seeked to:', formatTime(newTime), `(${Math.round(percent * 100)}%)`);
    } catch (error) {
      console.error('❌ Error seeking audio:', error);
    }
  };

  // Submit dictation for checking
  const checkWork = async () => {
    if (wordCount < 10) return; // Minimum word requirement
    
    setIsChecking(true);
    try {
      const response = await fetch('/api/dictation/attempts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          article_id: article.id,
          user_input: dictationText,
          replay_count: replayCount,
          time_spent: Math.floor((Date.now() - startTime) / 1000)
        })
      });

      if (response.ok) {
        const result = await response.json();
        setResults(result);
        setShowResults(true);
      } else {
        console.error('Failed to check dictation');
      }
    } catch (error) {
      console.error('Error checking dictation:', error);
    } finally {
      setIsChecking(false);
    }
  };

  // Save mistakes to vocabulary
  const saveMistakesToVocabulary = () => {
    if (results && results.mistakes) {
      onSaveMistakes(results.mistakes);
      onClose();
    }
  };

  // Replay incorrect parts
  const replayIncorrectParts = () => {
    // This would need more sophisticated audio segmentation
    // For now, just restart the audio
    audioRef.current.currentTime = 0;
    audioRef.current.play();
    setIsPlaying(true);
  };

  // Reset overlay when opening
  useEffect(() => {
    if (isOpen) {
      setDictationText('');
      setWordCount(0);
      setShowResults(false);
      setResults(null);
      setReplayCount(0);
      setCurrentTime(0);
      setIsPlaying(false);
      setStartTime(Date.now());
      
      // Initialize dictation mode
      if (article?.text_content) {
        const sentenceList = splitIntoSentences(article.text_content);
        setSentences(sentenceList);
        setCurrentSentence(0);
        setRepetitionCount(0);
        setDictationMode('paused');
        console.log(`📝 Initialized dictation with ${sentenceList.length} sentences:`, sentenceList);
      } else {
        console.log('❌ No text_content found in article:', article);
        setSentences([]);
        setCurrentSentence(0);
        setRepetitionCount(0);
        setDictationMode('paused');
      }
      
      // Debug audio path information
      console.log('🎧 DictationOverlay opened with article:', article);
      console.log('🎵 Audio path:', article?.audio_path);
      console.log('🔗 Constructed audio URL:', getAudioUrl());
      console.log('🌐 Current domain:', window.location.origin);
      
      // Additional debugging for Windows paths
      if (article?.audio_path && (article.audio_path.includes('\\') || article.audio_path.includes(':'))) {
        const uploadsIndex = article.audio_path.indexOf('uploads');
        if (uploadsIndex !== -1) {
          const relativePath = article.audio_path.substring(uploadsIndex + 8).replace(/\\/g, '/');
          console.log('🔄 Windows path detected');
          console.log('📁 Uploads index:', uploadsIndex);
          console.log('📂 Relative path:', relativePath);
          console.log('🎯 Final backend URL:', `${API_BASE_URL}/uploads/${relativePath}`);
          console.log('🔍 Expected file location:', `Backend/uploads/${relativePath}`);
        }
      }
      
      // Test if the constructed URL is accessible
      const audioUrl = getAudioUrl();
      if (audioUrl && audioUrl.startsWith(API_BASE_URL)) {
        console.log('🧪 Testing audio URL accessibility...');
        fetch(audioUrl, { method: 'HEAD' })
          .then(response => {
            if (response.ok) {
              console.log('✅ Audio file is accessible via backend');
            } else {
              console.log('❌ Audio file not accessible via backend:', response.status);
            }
          })
          .catch(error => {
            console.log('❌ Error testing backend audio URL:', error.message);
          });
      }
    }
  }, [isOpen, article]);

  // Helper function to split text into sentences
  const splitIntoSentences = (text) => {
    if (!text) {
      console.log('❌ No text provided to splitIntoSentences');
      return [];
    }
    
    console.log('📝 Splitting text into sentences:', text);
    
    // Split by sentence endings (., !, ?) followed by space or end of string
    const sentences = text
      .split(/(?<=[.!?])\s+/)
      .map(sentence => sentence.trim())
      .filter(sentence => sentence.length > 0);
    
    console.log('✅ Split into sentences:', sentences);
    return sentences;
  };

  if (!isOpen) return null;

  return (
    <div className="dictation-overlay">
      <div className="dictation-overlay-content">
        {/* Header */}
        <div className="overlay-header">
          <h2>🎧 Dictation Exercise: {article.title}</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="material-icons">close</i>
          </button>
        </div>

        {!showResults ? (
          <>
            {/* Audio Player Section */}
            <div className="audio-player-section">
              {/* Audio Status Display */}
              {audioLoading && (
                <div className="audio-status loading">
                  <i className="material-icons">downloading</i>
                  <span>Loading audio...</span>
                </div>
              )}
              
              {audioError && (
                <div className="audio-status error">
                  <i className="material-icons">error</i>
                  <span>{audioError}</span>
                  <div className="error-actions">
                    <button 
                      className="retry-btn" 
                      onClick={() => {
                        setAudioError(null);
                        setAudioLoading(true);
                        audioRef.current.load();
                      }}
                    >
                      <i className="material-icons">refresh</i>
                      Retry
                    </button>
                    <button 
                      className="test-url-btn" 
                      onClick={() => {
                        const audioUrl = getAudioUrl();
                        console.log('🔗 Testing audio URL:', audioUrl);
                        // Test if the URL is accessible
                        fetch(audioUrl, { method: 'HEAD' })
                          .then(response => {
                            if (response.ok) {
                              console.log('✅ Audio file is accessible');
                              setAudioError('Audio file is accessible, trying to load...');
                              setTimeout(() => {
                                setAudioError(null);
                                setAudioLoading(true);
                                audioRef.current.load();
                              }, 1000);
                            } else {
                              console.log('❌ Audio file not accessible:', response.status);
                              setAudioError(`Audio file not accessible (${response.status})`);
                            }
                          })
                          .catch(error => {
                            console.log('❌ Error testing audio URL:', error);
                            setAudioError(`Network error: ${error.message}`);
                          });
                      }}
                    >
                      <i className="material-icons">link</i>
                      Test URL
                    </button>
                  </div>
                </div>
              )}

              <div className="audio-controls">
                 {/* Standard Audio Controls */}
                <div className="standard-audio-controls">
                  <button 
                    className="control-btn" 
                    onClick={togglePlay}
                    disabled={audioLoading || audioError}
                  >
                    <i className="material-icons">
                      {isPlaying ? 'pause' : 'play_arrow'}
                    </i>
                    {isPlaying ? 'Pause' : 'Play'}
                  </button>
                  
                  {/* Dedicated Slow Dictation Button */}
                  <button 
                    className="control-btn slow-dictation-btn" 
                    onClick={() => {
                      const newSpeed = 0.25;
                      setPlaybackSpeed(newSpeed);
                      if (audioRef.current) {
                        audioRef.current.playbackRate = newSpeed;
                      }
                      console.log('🐌 Slow dictation speed set to:', newSpeed + 'x');
                    }}
                    disabled={audioLoading || audioError}
                    title="Set to 0.25x - Official dictation speed"
                  >
                    <i className="material-icons">slow_motion_video</i>
                    Slow Dictation
                  </button>
                  
                  <button 
                    className="control-btn" 
                    onClick={changeDictationSpeed}
                    disabled={audioLoading || audioError}
                    title={`Current speed: ${playbackSpeed}x. Click to change.`}
                  >
                    <i className="material-icons">speed</i>
                    <span className="speed-display">{playbackSpeed}x</span>
                  </button>
                  
                  <button 
                    className={`control-btn ${autoPause ? 'active' : ''}`}
                    onClick={() => setAutoPause(!autoPause)}
                    disabled={audioLoading || audioError}
                    title={autoPause ? 'Auto-pause is ON - Audio will pause after each sentence' : 'Auto-pause is OFF - Audio plays continuously'}
                  >
                    <i className="material-icons">timer</i>
                    {autoPause ? 'Auto-pause ON' : 'Auto-pause'}
                  </button>
                </div>
                
                {/* Audio Progress Bar */}
                <div className="audio-progress" onClick={seekTo}>
                  <div 
                    className="audio-progress-fill" 
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  />
                  <div 
                    className="audio-progress-thumb" 
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                  />
                </div>

                {/* Time Display */}
                <div className="audio-time">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>

                {/* Audio Element */}
                <audio 
                  ref={audioRef}
                  src={getAudioUrl()}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  preload="metadata"
                  crossOrigin="anonymous"
                >
                  {/* Fallback sources for different audio formats */}
                  <source src={getAudioUrl()} type="audio/mpeg" />
                  <source src={getAudioUrl()} type="audio/wav" />
                  <source src={getAudioUrl()} type="audio/ogg" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>

            {/* Text Input Section */}
            <div className="text-input-section">
              <label htmlFor="dictationInput">Type what you hear:</label>
              <textarea
                ref={textareaRef}
                id="dictationInput"
                value={dictationText}
                onChange={(e) => setDictationText(e.target.value)}
                placeholder="Type what you hear here..."
                rows={8}
                className="dictation-textarea"
              />
              <div className="input-stats">
                <span className="word-count">Words: {wordCount}</span>
                <span className="char-count">Characters: {dictationText.length}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button 
                className="check-work-btn"
                disabled={wordCount < 10 || isChecking}
                onClick={checkWork}
              >
                <i className="material-icons">check_circle</i>
                {isChecking ? 'Checking...' : 'Check My Work'}
              </button>
              
              <button className="exit-btn" onClick={onClose}>
                <i className="material-icons">exit_to_app</i>
                Exit
              </button>
            </div>
          </>
        ) : (
          /* Results Section */
          <div className="results-section">
            <div className="results-header">
              <h3>📊 Dictation Results</h3>
              <div className="score-display">
                <span className="score-label">Score:</span>
                <span className="score-value">{results.score}%</span>
              </div>
              <button className="close-btn results-close-btn" onClick={onClose}>
                <i className="material-icons">close</i>
              </button>
            </div>

            {results.mistakes && results.mistakes.length > 0 && (
              <div className="mistakes-section">
                <h4>❌ Mistakes Found:</h4>
                {results.mistakes.map((mistake, index) => (
                  <div key={index} className="mistake-item">
                    <div className="mistake-word">
                      <span className="wrong-spelling">{mistake.user_word}</span>
                      <span className="arrow">→</span>
                      <span className="correct-spelling">{mistake.correct_word}</span>
                    </div>
                    {mistake.meaning && (
                      <div className="word-meaning">
                        <strong>Meaning:</strong> {mistake.meaning}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {results.missing_words && results.missing_words.length > 0 && (
              <div className="missing-words-section">
                <h4>🟡 Missing Words:</h4>
                <div className="missing-words-list">
                  {results.missing_words.map((wordObj, index) => (
                    <span key={index} className="missing-word">{wordObj.word}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="results-actions">
              <button className="replay-incorrect-btn" onClick={replayIncorrectParts}>
                <i className="material-icons">replay</i>
                Replay Incorrect Parts
              </button>
              
              <button className="save-mistakes-btn" onClick={saveMistakesToVocabulary}>
                <i className="material-icons">save</i>
                Save Mistakes to My Vocabulary
              </button>
              
              <button className="new-dictation-btn" onClick={() => setShowResults(false)}>
                <i className="material-icons">refresh</i>
                Try Another Dictation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DictationOverlay;